import {
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import BarChart10FilledIcon from "@redotech/redo-web/icon-old/bar-chart-10-filled.svg";
import BarChart10Icon from "@redotech/redo-web/icon-old/bar-chart-10.svg";
import { memo, useContext } from "react";
import { useMatch } from "react-router-dom";
import { OrderManagementSuiteStatusesContext } from "../../../../redo-suite/order-management-cloud";

export const ReturnsAnalyticsItem = memo(function ReturnsAnalyticsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);

  const { returnsEnabled, packageProtectionEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );

  const hasPermissionToViewOrderFulfillment = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
  );
  const hasPermissionToViewReturnsClaims = useHasPermission(
    ReturnsAndClaimsPermissions.VIEW_RETURN_AND_CLAIM_ANALYTICS,
  );
  const hasPermissionToViewReports = useHasPermission(
    OrderManagementPermissions.VIEW_REPORTS,
  );

  const showReturnsClaims =
    (returnsEnabled || packageProtectionEnabled) &&
    hasPermissionToViewReturnsClaims;

  const showManagedReturns =
    !!team.settings.redoManagedReturns?.enabled &&
    hasPermissionToViewOrderFulfillment;

  const returnsClaimsSelected = useMatch(
    `${routes.analyticsReturnsClaimsHref}/*`,
  );
  const reportsSelected = useMatch(`${routes.analyticsReportsHref}/*`);
  const managedReturnsSelected = useMatch(
    `${routes.analyticsManagedReturnsHref}/*`,
  );
  const customReportsHref = `${routes.analyticsReportsHref}/custom`;
  const anyChildSelected =
    returnsClaimsSelected || reportsSelected || managedReturnsSelected;

  const shouldShowAnalytics =
    showReturnsClaims || hasPermissionToViewReports || showManagedReturns;

  if (!shouldShowAnalytics) {
    return null;
  }

  const defaultRoute = showReturnsClaims
    ? routes.analyticsReturnsClaimsHref
    : routes.analyticsReportsHref;

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: defaultRoute }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={BarChart10FilledIcon}
      friendlyName={t("Analytics")}
      id="returns-analytics"
      UnfilledIcon={BarChart10Icon}
    >
      <ReturnsCustomReportsItem
        hidden={!hasPermissionToViewReports}
        href={customReportsHref}
      />
      <ReturnsManagedReturnsAnalyticsItem hidden={!showManagedReturns} />
      <ReturnsReportsItem hidden={!hasPermissionToViewReports} />
      <ReturnsClaimsAnalyticsItem hidden={!showReturnsClaims} />
    </RedoSecondaryNavItem>
  );
});

const ReturnsCustomReportsItem = memo(function ReturnsCustomReportsItem({
  href,
  hidden,
}: {
  href: string;
  hidden: boolean;
}) {
  const { t } = useTranslation();
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      beta
      friendlyName={t("Custom reports")}
      hidden={hidden}
      id="returns-analytics-custom-reports"
    />
  );
});

const ReturnsManagedReturnsAnalyticsItem = memo(
  function ReturnsManagedReturnsAnalyticsItem({ hidden }: { hidden: boolean }) {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.analyticsManagedReturnsHref }}
        friendlyName={t("Managed returns")}
        hidden={hidden}
        id="returns-analytics-managed-returns"
      />
    );
  },
);

const ReturnsReportsItem = memo(function ReturnsReportsItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.analyticsReportsHref }}
      friendlyName={t("Reports")}
      hidden={hidden}
      id="returns-analytics-reports"
    />
  );
});

const ReturnsClaimsAnalyticsItem = memo(function ReturnsClaimsAnalyticsItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.analyticsReturnsClaimsHref }}
      friendlyName={t("Returns & claims")}
      hidden={hidden}
      id="returns-analytics-returns-claims"
    />
  );
});
